import { useEffect, useRef, useState } from 'react';
import type { ParticipantPublic } from '@momentum/shared';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { characterColor } from '../renderers/assets.ts';
import { createPlayerFace, hash } from '../renderers/playerFace.ts';
import { createPotatoActor } from '../renderers/hotPotatoActors.ts';

const berryUrl = new URL('../../../../assets/board/models/berry-jump-hop.glb', import.meta.url).href;
const receiverUrl = new URL('../../../../assets/characters/animated-client/models/client-animated.glb', import.meta.url).href;

export interface LobbySceneProps {
  participants: ParticipantPublic[];
  reducedMotion: boolean;
  selfId?: string;
  emotes?: Record<string, { emoteId: string }>;
  speakingParticipantIds?: readonly string[];
  hostSpeaking?: boolean;
  hostMessage?: string;
}

type PlayerActor = {
  id: string;
  root: THREE.Object3D;
  face: ReturnType<typeof createPlayerFace>;
  mixer: THREE.AnimationMixer | null;
  baseY: number;
  joinedAt: number;
  seat: number;
  characterSlot?: string | null;
  baseRotationY: number;
  baseScale: THREE.Vector3;
  connection: string;
};

const seats: readonly (readonly [number, number, number])[] = [
  [-2.32, .5, .62], [2.32, .5, .62], [-1.58, .5, 1.72], [1.58, .5, 1.72], [-.58, .5, 2.18], [.58, .5, 2.18],
];

function cloneModel(source: THREE.Object3D, tint?: string) {
  const clone = source.clone(true);
  clone.traverse(object => {
    if (!(object instanceof THREE.Mesh)) return;
    object.geometry = object.geometry.clone();
    object.castShadow = true;
    object.receiveShadow = true;
    const materials = (Array.isArray(object.material) ? object.material : [object.material]).map(material => {
      const copy = material.clone();
      if (tint && copy instanceof THREE.MeshStandardMaterial && copy.name.startsWith('PlayerColor_')) copy.color.set(tint);
      return copy;
    });
    object.material = Array.isArray(object.material) ? materials : materials[0]!;
  });
  return clone;
}

function fitHeight(model: THREE.Object3D, height: number, floorY: number) {
  const box = new THREE.Box3().setFromObject(model);
  const scale = height / Math.max(.001, box.getSize(new THREE.Vector3()).y);
  model.scale.setScalar(scale);
  model.position.y = floorY - box.min.y * scale;
}

function disposeObject(root: THREE.Object3D) {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  root.traverse(object => {
    if (!(object instanceof THREE.Mesh)) return;
    geometries.add(object.geometry);
    (Array.isArray(object.material) ? object.material : [object.material]).forEach(material => materials.add(material));
  });
  geometries.forEach(geometry => geometry.dispose());
  materials.forEach(material => material.dispose());
}

function fallbackBerry(color: string) {
  const group = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.SphereGeometry(.38, 24, 18).scale(1, 1.1, .88),
    new THREE.MeshStandardMaterial({ color, roughness: .72 }),
  );
  body.position.y = .43;
  const eyeMaterial = new THREE.MeshStandardMaterial({ color: '#17212b', roughness: .55 });
  for (const x of [-.13, .13]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(.04, 12, 8), eyeMaterial);
    eye.position.set(x, .5, .33); group.add(eye);
  }
  group.add(body);
  return group;
}

function makeRing(color: THREE.ColorRepresentation, opacity: number, radius = .47) {
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(radius - .035, radius, 48),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity, side: THREE.DoubleSide, depthWrite: false }),
  );
  ring.rotation.x = -Math.PI / 2;
  return ring;
}

export function LobbyScene({ participants, reducedMotion, selfId, emotes = {}, speakingParticipantIds = [], hostSpeaking = false, hostMessage }: LobbySceneProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const live = useRef({ participants, reducedMotion, selfId, emotes, speakingParticipantIds, hostSpeaking, hostMessage });
  live.current = { participants, reducedMotion, selfId, emotes, speakingParticipantIds, hostSpeaking, hostMessage };
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const currentMount = hostRef.current;
    if (!currentMount) return;
    const mount = currentMount;
    let stopped = false;
    let animationFrame = 0;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    } catch {
      setFailed(true);
      return;
    }
    setFailed(false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.08;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.domElement.setAttribute('aria-hidden', 'true');
    renderer.domElement.style.cssText = 'display:block;width:100%;height:100%;';
    mount.append(renderer.domElement);

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0xb9ddeb, 10, 19);
    const camera = new THREE.PerspectiveCamera(31, 1, .1, 40);
    const target = new THREE.Vector3(0, .68, .65);
    scene.add(new THREE.HemisphereLight(0xf4fbff, 0x6f91a0, 2.1));
    const sun = new THREE.DirectionalLight(0xfff0d7, 3.25);
    sun.position.set(-5, 9, 7); sun.castShadow = true; sun.shadow.mapSize.set(1024, 1024); sun.shadow.radius = 5;
    Object.assign(sun.shadow.camera, { left: -5, right: 5, top: 5, bottom: -5, near: .1, far: 24 });
    scene.add(sun);
    const rim = new THREE.DirectionalLight(0xa9ccff, 1.1); rim.position.set(5, 4, -5); scene.add(rim);

    const waterMaterial = new THREE.MeshStandardMaterial({ color: '#73b8cf', roughness: .34, metalness: .08, transparent: true, opacity: .9 });
    const water = new THREE.Mesh(new THREE.CircleGeometry(4.72, 80), waterMaterial);
    water.rotation.x = -Math.PI / 2; water.position.y = -.09; water.scale.y = .69; water.receiveShadow = true; scene.add(water);
    const waterEdge = new THREE.Mesh(
      new THREE.TorusGeometry(4.54, .035, 8, 96),
      new THREE.MeshBasicMaterial({ color: '#caedf4', transparent: true, opacity: .36 }),
    );
    waterEdge.rotation.x = Math.PI / 2; waterEdge.position.y = -.075; waterEdge.scale.y = .69; scene.add(waterEdge);
    const island = new THREE.Mesh(
      new THREE.CylinderGeometry(3.38, 3.58, .37, 64),
      new THREE.MeshStandardMaterial({ color: '#eee7d9', roughness: .78, metalness: .02 }),
    );
    island.position.y = .11; island.scale.z = .72; island.receiveShadow = true; island.castShadow = true; scene.add(island);
    const islandTop = new THREE.Mesh(
      new THREE.CircleGeometry(3.27, 64),
      new THREE.MeshStandardMaterial({ color: '#f7f0e4', roughness: .88 }),
    );
    islandTop.rotation.x = -Math.PI / 2; islandTop.position.y = .305; islandTop.scale.y = .72; islandTop.receiveShadow = true; scene.add(islandTop);
    const ceramicRim = new THREE.Mesh(
      new THREE.TorusGeometry(3.27, .065, 14, 96),
      new THREE.MeshStandardMaterial({ color: '#fff8eb', roughness: .62 }),
    );
    ceramicRim.rotation.x = Math.PI / 2; ceramicRim.position.y = .303; ceramicRim.scale.y = .72; ceramicRim.castShadow = true; scene.add(ceramicRim);

    // A few restrained map-like landmarks give the gathering space depth without turning it into a game table.
    const rockMaterial = new THREE.MeshStandardMaterial({ color: '#c6bda9', roughness: .96 });
    [[-2.7, -.25, .16], [2.78, -.4, .12], [-2.46, -1.15, .1]].forEach(([x, z, scale], index) => {
      const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(1, 0), rockMaterial);
      rock.scale.set(scale * 1.3, scale, scale); rock.position.set(x, .36 + scale * .35, z); rock.rotation.y = index; rock.castShadow = true; scene.add(rock);
    });
    const tuftMaterial = new THREE.MeshStandardMaterial({ color: '#8ab68d', roughness: .9 });
    for (const [x, z] of [[-2.8, .58], [2.75, .42], [-2.2, -1.25], [2.25, -1.12]] as const) {
      const tuft = new THREE.Group();
      for (let i = 0; i < 3; i += 1) {
        const leaf = new THREE.Mesh(new THREE.ConeGeometry(.055, .3 + i * .03, 8), tuftMaterial);
        leaf.position.set((i - 1) * .08, .15, 0); leaf.rotation.z = (i - 1) * -.24; leaf.castShadow = true; tuft.add(leaf);
      }
      tuft.position.set(x, .31, z); scene.add(tuft);
    }

    const playerActors = new Map<string, PlayerActor>();
    const readyRings = new Map<string, THREE.Mesh>();
    const vacantMarkers = new Map<number, THREE.Group>();
    const joinedClock = performance.now();

    let receiverActor: ReturnType<typeof createPotatoActor> | null = null;
    let berrySource: THREE.Object3D | null = null;
    let berryClips: THREE.AnimationClip[] = [];
    let assetsReady = false;
    let lastRosterKey = '';

    function removeActor(actor: PlayerActor) {
      actor.mixer?.stopAllAction(); actor.mixer?.uncacheRoot(actor.root);
      scene.remove(actor.root); disposeObject(actor.root); playerActors.delete(actor.id);
      const ring = readyRings.get(actor.id);
      if (ring) { scene.remove(ring); disposeObject(ring); readyRings.delete(actor.id); }
    }

    function reconcileRoster(now: number) {
      if (!assetsReady) return;
      const roster = live.current.participants.slice(0, 6);
      const rosterKey = roster.map(participant => `${participant.id}:${participant.characterSlot ?? ''}:${participant.connection}`).join('|');
      if (rosterKey === lastRosterKey) return;
      lastRosterKey = rosterKey;
      const ids = new Set(roster.map(participant => participant.id));
      playerActors.forEach(actor => { if (!ids.has(actor.id)) removeActor(actor); });
      roster.forEach((participant, index) => {
        let actor = playerActors.get(participant.id);
        if (actor && actor.characterSlot !== participant.characterSlot) { removeActor(actor); actor = undefined; }
        const [x, , z] = seats[index]!;
        if (!actor) {
          const root = berrySource ? cloneModel(berrySource, characterColor(participant.characterSlot)) : fallbackBerry(characterColor(participant.characterSlot));
          fitHeight(root, .82, .34); root.position.x = x; root.position.z = z;
          root.rotation.y = Math.PI + (x < 0 ? -.15 : .15); scene.add(root);
          const mixer = berryClips.length ? new THREE.AnimationMixer(root) : null;
          const idle = mixer ? THREE.AnimationClip.findByName(berryClips, 'idle') : undefined;
          if (idle && mixer) mixer.clipAction(idle).play();
          actor = { id: participant.id, root, face: createPlayerFace(root, participant.id), mixer, baseY: root.position.y,
            joinedAt: now + index * 55, seat: index, characterSlot: participant.characterSlot,
            baseRotationY: root.rotation.y, baseScale: root.scale.clone(), connection: participant.connection };
          playerActors.set(participant.id, actor);
          const readyRing = makeRing(characterColor(participant.characterSlot), participant.ready ? .8 : .12, .47);
          readyRing.position.set(x, .332, z); scene.add(readyRing); readyRings.set(participant.id, readyRing);
        }
        if (!actor) return;
        if (actor.connection !== participant.connection && participant.connection === 'connected') actor.joinedAt = now;
        actor.connection = participant.connection;
        actor.seat = index; actor.root.position.x = x; actor.root.position.z = z;
        actor.baseRotationY = Math.PI + (x < 0 ? -.15 : .15);
        const ring = readyRings.get(participant.id); if (ring) ring.position.set(x, .332, z);
      });
      for (let index = 0; index < seats.length; index += 1) {
        const occupied = index < roster.length; const existing = vacantMarkers.get(index);
        if (occupied && existing) { scene.remove(existing); disposeObject(existing); vacantMarkers.delete(index); }
        if (!occupied && !existing) {
          const [x, , z] = seats[index]!; const marker = new THREE.Group();
          const vacant = makeRing('#d7cfbf', .5, .39); vacant.position.set(x, .326, z); marker.add(vacant);
          const dot = new THREE.Mesh(new THREE.CircleGeometry(.08, 24), new THREE.MeshBasicMaterial({ color: '#cfc6b6', transparent: true, opacity: .56 }));
          dot.rotation.x = -Math.PI / 2; dot.position.set(x, .334, z); marker.add(dot); scene.add(marker); vacantMarkers.set(index, marker);
        }
      }
    }

    Promise.allSettled([new GLTFLoader().loadAsync(berryUrl), new GLTFLoader().loadAsync(receiverUrl)]).then(results => {
      if (stopped) {
        results.forEach(result => { if (result.status === 'fulfilled') disposeObject(result.value.scene); });
        return;
      }
      const berryResult = results[0];
      const receiverResult = results[1];
      berrySource = berryResult.status === 'fulfilled' ? berryResult.value.scene : null;
      berryClips = berryResult.status === 'fulfilled' ? berryResult.value.animations : [];
      assetsReady = true; reconcileRoster(joinedClock);
      const receiver = receiverResult.status === 'fulfilled' ? cloneModel(receiverResult.value.scene) : fallbackBerry('#242b31');
      fitHeight(receiver, .78, .34); receiver.position.set(0, receiver.position.y, -1.32); receiver.rotation.y = Math.PI; scene.add(receiver);
      receiverActor = createPotatoActor(receiver, receiverResult.status === 'fulfilled' ? receiverResult.value.animations : [], true);
      if (receiverResult.status === 'fulfilled') disposeObject(receiverResult.value.scene);
    });

    const clock = new THREE.Clock();
    let elapsed = 0;
    let lastFrame = -Infinity;
    function render(now: number) {
      if (stopped) return;
      animationFrame = requestAnimationFrame(render);
      if (document.hidden || now - lastFrame < 33) return;
      lastFrame = now;
      const state = live.current;
      reconcileRoster(now);
      const delta = Math.min(.05, clock.getDelta()); elapsed += delta;
      waterMaterial.color.setHSL(.54 + (state.reducedMotion ? 0 : Math.sin(elapsed * .3) * .004), .48, .62);
      playerActors.forEach(actor => {
        const participant = state.participants.find(item => item.id === actor.id);
        const emote = state.emotes[actor.id]?.emoteId;
        const speaking = state.speakingParticipantIds.includes(actor.id);
        const joinProgress = state.reducedMotion ? 1 : THREE.MathUtils.clamp((now - actor.joinedAt) / 620, 0, 1);
        const joinHop = Math.sin(joinProgress * Math.PI) * .25;
        const emotePhase = (now + hash(`${actor.id}:${emote ?? ''}`)) / 240;
        let emoteHop = 0; let tilt = 0; let turn = 0; let squash = 1;
        if (!state.reducedMotion && emote === 'wave') { tilt = Math.sin(emotePhase * 1.35) * .16; turn = Math.sin(emotePhase * .68) * .12; }
        if (!state.reducedMotion && emote === 'bounce') {
          emoteHop = Math.abs(Math.sin(emotePhase * 1.2)) * .15;
          tilt = Math.sin(emotePhase * 1.2) * .11;
          turn = Math.sin(emotePhase * .6) * .2;
          squash = 1 + Math.cos(emotePhase * 2.4) * .045;
        }
        if (!state.reducedMotion && emote === 'spin') turn = (emotePhase * 1.45) % (Math.PI * 2);
        if (!state.reducedMotion && emote === 'cheer') { emoteHop = Math.max(0, Math.sin(emotePhase * 1.7)) * .17; squash = 1 + Math.sin(emotePhase * 1.7) * .06; tilt = Math.sin(emotePhase * 3.4) * .045; }
        const speakingBob = !state.reducedMotion && speaking ? Math.sin(elapsed * 5.2 + hash(actor.id)) * .018 : 0;
        actor.root.position.y = actor.baseY + joinHop + emoteHop + speakingBob + (!state.reducedMotion ? Math.sin(elapsed * 1.35 + hash(actor.id) % 9) * .009 : 0);
        actor.root.rotation.y = actor.baseRotationY + turn;
        actor.root.rotation.z = tilt + (!state.reducedMotion && speaking ? Math.sin(elapsed * 3.1 + hash(actor.id)) * .035 : 0);
        actor.root.scale.copy(actor.baseScale); actor.root.scale.y *= squash;
        actor.mixer?.update(state.reducedMotion ? 0 : delta);
        actor.face.update(now, speaking ? 'anticipating' : 'idle', state.reducedMotion, joinProgress < 1 || Boolean(emote));
        const ring = readyRings.get(actor.id);
        if (ring) {
          const ready = Boolean(participant?.ready);
          (ring.material as THREE.MeshBasicMaterial).opacity = ready ? .72 + (state.reducedMotion ? 0 : Math.sin(elapsed * 2.1) * .08) : .11;
          ring.scale.setScalar(actor.id === state.selfId ? 1.09 : 1);
        }
      });
      receiverActor?.update(state.hostSpeaking ? 'speaking' : 'pleased', elapsed, 2, state.reducedMotion, 0);
      if (bubbleRef.current) {
        const anchor = new THREE.Vector3(0, 1.52, -1.32).project(camera);
        const width = mount.clientWidth || 480; const height = mount.clientHeight || 360;
        bubbleRef.current.style.left = `${THREE.MathUtils.clamp((anchor.x + 1) * width / 2, 110, Math.max(110, width - 110))}px`;
        const bubbleHeight = bubbleRef.current.offsetHeight || 60;
        bubbleRef.current.style.top = `${THREE.MathUtils.clamp((1 - anchor.y) * height / 2 - 12, bubbleHeight + 8, height - 8)}px`;
      }
      renderer.render(scene, camera);
    }

    function resize() {
      const width = mount.clientWidth || 480;
      const height = mount.clientHeight || 360;
      renderer.setSize(width, height, false); camera.aspect = width / height;
      const halfHeight = Math.max(2.1, 4.55 / camera.aspect);
      const distance = halfHeight / Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
      camera.position.copy(target).add(new THREE.Vector3(0, .43, .9).normalize().multiplyScalar(distance));
      camera.lookAt(target); camera.updateProjectionMatrix();
    }
    const observer = new ResizeObserver(resize); observer.observe(mount); resize();
    renderer.domElement.addEventListener('webglcontextlost', event => { event.preventDefault(); setFailed(true); });
    animationFrame = requestAnimationFrame(render);

    return () => {
      stopped = true;
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
      playerActors.forEach(actor => { actor.mixer?.stopAllAction(); actor.mixer?.uncacheRoot(actor.root); });
      receiverActor?.dispose();
      if (berrySource) disposeObject(berrySource);
      disposeObject(scene);
      renderer.dispose();
      renderer.forceContextLoss();
      renderer.domElement.remove();
    };
  }, []);

  return (
    <div ref={hostRef} role="group" aria-label="Players gathering on the Momentum island" style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      {hostMessage ? <div ref={bubbleRef} role="status" aria-live="polite" style={{ position: 'absolute', zIndex: 2, transform: 'translate(-50%, -100%)', width: 'min(300px, calc(100% - 24px))', padding: '9px 13px', borderRadius: 14, background: 'rgba(255,255,255,.96)', boxShadow: '0 8px 24px rgba(35,55,70,.16)', color: '#1f2b34', fontSize: 13, lineHeight: 1.35, textAlign: 'center', pointerEvents: 'none' }}>{hostMessage}</div> : null}
      {failed ? <div role="status" style={{ display: 'grid', minHeight: 280, height: '100%', placeItems: 'center', color: 'var(--text-muted, #60707b)' }}>The gathering space is ready.</div> : null}
    </div>
  );
}
